/**
 * SearchBar Component - Story 7.5: Implement Product Search
 * Reusable search bar component for marketplace header
 *
 * Features:
 * - Search input with debounced API calls
 * - Clear button for quick reset
 * - Accessible keyboard navigation
 * - Mobile responsive
 * - Integration with marketplace filters
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * SearchBar Props
 */
export interface SearchBarProps {
  /** Initial search query value */
  initialValue?: string;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Callback when search is submitted */
  onSearch: (query: string) => void;
  /** Debounce delay in milliseconds (default: 300ms) */
  debounceDelay?: number;
  /** Custom CSS class name */
  className?: string;
  /** Whether to show search button or submit on Enter */
  showSearchButton?: boolean;
  /** Auto focus on mount */
  autoFocus?: boolean;
}

/**
 * SearchBar Component
 * Reusable search input with debouncing and clear functionality
 *
 * @component
 * @example
 * <SearchBar
 *   placeholder="Search products..."
 *   onSearch={(query) => navigate(`/search?q=${query}`)}
 *   debounceDelay={300}
 * />
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  initialValue = '',
  placeholder = 'Search products...',
  onSearch,
  debounceDelay = 300,
  className,
  showSearchButton = false,
  autoFocus = false,
}) => {
  // ============================================================================
  // State
  // ============================================================================

  const [value, setValue] = useState<string>(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Auto focus input if requested
   */
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  /**
   * Debounced search - emit when value changes
   */
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timeout
    debounceTimerRef.current = setTimeout(() => {
      onSearch(value.trim());
    }, debounceDelay);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, onSearch, debounceDelay]);

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Handle input change
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  /**
   * Handle clear button click
   */
  const handleClear = useCallback(() => {
    setValue('');
    inputRef.current?.focus();
  }, []);

  /**
   * Handle form submission (Enter key or Search button)
   */
  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearch(value.trim());
  }, [value, onSearch]);

  /**
   * Handle input focus
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  /**
   * Handle input blur
   */
  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('w-full', className)}
      role="search"
      aria-label="Product search"
    >
      <div
        className={cn(
          'relative flex items-center gap-2',
          'bg-white border border-gray-300 rounded-lg',
          'transition-all duration-200 ease-in-out',
          isFocused && 'border-blue-500 ring-2 ring-blue-200'
        )}
      >
        {/* Search Icon */}
        <Search
          className="h-5 w-5 text-gray-400 ml-3"
          aria-hidden="true"
        />

        {/* Input */}
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={cn(
            'border-none shadow-none focus-visible:ring-0',
            'bg-transparent text-gray-900',
            'placeholder:text-gray-500',
            'px-0'
          )}
          aria-label="Search products"
          aria-placeholder={placeholder}
        />

        {/* Clear Button */}
        {value && (
          <Button
            type="button"
            onClick={handleClear}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 mr-2 hover:bg-gray-100"
            aria-label="Clear search"
            aria-controls="search-input"
          >
            <X className="h-4 w-4 text-gray-500" />
          </Button>
        )}

        {/* Search Button (optional) */}
        {showSearchButton && (
          <Button
            type="submit"
            size="sm"
            className="mr-2 h-8 px-3"
            aria-label="Search"
          >
            Search
          </Button>
        )}
      </div>

      {/* Screen reader announcement */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {value && `Search query: ${value}`}
      </div>
    </form>
  );
};

export default SearchBar;
